// src/components/SidebarLeft.tsx
import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";          // ← ОБЯЗАТЕЛЬНО добавь useState
import { 
  Home, 
  CheckSquare, 
  FileText, 
  BarChart2, 
  Calendar, 
  HardDrive, 
  Settings, 
  LogOut, 
  Circle,
  ChevronDown,
  Shield,
  ClipboardList          // ← добавили иконку для «Акции»
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useNotificationStore } from "@/store/notificationStore";
import { useCalendarEventStore } from "@/store/calendarEventStore";
import { toast } from "@/hooks/use-toast";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import OrgStructureDialog from "@/components/OrgStructureDialog";
import { isToday, parseISO } from "date-fns";
import { cn } from "@/lib/utils";

function initials(fullName: string): string {
  return fullName.split(" ").map(n => n[0]).join("").toUpperCase();
}

export default function SidebarLeft() {
  const { user, organization, structure } = useAuthStore();
  
  const { 
    unreadTasksCount, 
    unreadMessagesCount, 
    unreadDocumentsCount,
    init: initNotifications, 
    cleanup: cleanupNotifications 
  } = useNotificationStore();

  const { events } = useCalendarEventStore();
  const navigate = useNavigate();
  const [orgOpen, setOrgOpen] = useState(false);

  useEffect(() => {
    initNotifications();
    return () => cleanupNotifications();
  }, [initNotifications, cleanupNotifications]);

  const handleLogout = () => {
    useAuthStore.getState().logout();
    toast({ title: "Вы вышли из системы" });
    navigate("/login");
  };

  if (!user) return null;

  const isPavlik = String(user?.id) === "4" || user?.numericId === 4;
  const isAdmin = user?.isAdmin === true;

  const totalTasksUnread = unreadTasksCount + unreadMessagesCount;
  const todayEventsCount = events.filter((ev) => isToday(parseISO(ev.date))).length;

  // Динамическая структура с fallback
  const displayStructure = structure && structure.length > 0 
    ? structure 
    : [
        { id: "legacy-1", name: "Заместитель руководителя", personName: "Андреев Алексей Евгеньевич", icon: "Crown" },
        { id: "legacy-2", name: "Начальник отдела", personName: "Багаутдинов Ахмет Айратович", icon: "Bomb" },
      ];

  return (
    <aside className="fixed left-0 top-0 h-full w-80 bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-950/90 backdrop-blur-2xl z-50 flex flex-col border-r border-purple-700/40">
      
      <div className="px-8 pt-10 pb-8">
        <div className="flex justify-center mb-8">
          <img 
            src="/images/logo.png" 
            alt="ОРВ ВСКС" 
            className="w-70 h-auto drop-shadow-2xl"   
            onError={(e) => (e.currentTarget.src = "/vite.svg")}
          />
        </div>

        <Dialog open={orgOpen} onOpenChange={setOrgOpen}>
          <DialogTrigger asChild>
            <button className="w-full bg-gradient-to-r from-purple-600/50 to-pink-600/50 hover:from-purple-600/70 hover:to-pink-600/70 backdrop-blur-xl border border-purple-500/60 text-purple-100 font-medium py-5 px-8 rounded-3xl transition-all duration-500 shadow-2xl shadow-purple-600/40 flex items-center justify-between group">
              <span className="text-lg tracking-wide">
                {organization?.shortName || "Структура ОРВ"}
              </span>
              <ChevronDown className="h-6 w-6 text-purple-300 group-hover:rotate-180 transition-transform duration-500" />
            </button>
          </DialogTrigger>
          <OrgStructureDialog 
            open={orgOpen} 
            onOpenChange={setOrgOpen}
            structure={displayStructure}
          />
        </Dialog>
      </div>

      <nav className="flex-1 px-6 space-y-6">
        <NavLink 
          to="/dashboard" 
          className={({ isActive }) => `flex items-center gap-5 px-8 py-5 rounded-3xl transition-all duration-500 ${isActive ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl shadow-purple-600/60" : "text-purple-300 hover:bg-purple-600/40"}`}
        >
          <Home className="h-7 w-7" />
          <span className="text-xl font-medium">Дашборд</span>
        </NavLink>

        <NavLink 
          to="/tasks" 
          className={({ isActive }) => `relative flex items-center gap-5 px-8 py-5 rounded-3xl transition-all duration-500 ${isActive ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl shadow-purple-600/60" : "text-purple-300 hover:bg-purple-600/40"}`}
        >
          <CheckSquare className="h-7 w-7" />
          <span className="text-xl font-medium">Задачник</span>
          
          {totalTasksUnread > 0 && (
            <Badge className="absolute -top-3 -right-3 bg-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center text-lg font-bold shadow-2xl shadow-red-600/60 animate-pulse border-4 border-purple-900/80">
              {totalTasksUnread > 99 ? "99+" : totalTasksUnread}
            </Badge>
          )}
        </NavLink>

        <NavLink 
          to="/documents" 
          className={({ isActive }) => `relative flex items-center gap-5 px-8 py-5 rounded-3xl transition-all duration-500 ${isActive ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl shadow-purple-600/60" : "text-purple-300 hover:bg-purple-600/40"}`}
        >
          <FileText className="h-7 w-7" />
          <span className="text-xl font-medium">Документы</span>
          
          {unreadDocumentsCount > 0 && (
            <Badge className="absolute -top-3 -right-3 bg-amber-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-base font-bold shadow-lg animate-pulse">
              {unreadDocumentsCount > 99 ? "99+" : unreadDocumentsCount}
            </Badge>
          )}
        </NavLink>

        <NavLink to="/reports" className={({ isActive }) => `flex items-center gap-5 px-8 py-5 rounded-3xl transition-all duration-500 ${isActive ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl shadow-purple-600/60" : "text-purple-300 hover:bg-purple-600/40"}`}>
          <BarChart2 className="h-7 w-7" />
          <span className="text-xl font-medium">Отчёты</span>
        </NavLink>

        {/* ===== НОВЫЙ ПУНКТ «АКЦИИ» ===== */}
        <NavLink 
          to="/forms" 
          className={({ isActive }) => `flex items-center gap-5 px-8 py-5 rounded-3xl transition-all duration-500 ${isActive ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl shadow-purple-600/60" : "text-purple-300 hover:bg-purple-600/40"}`}
        >
          <ClipboardList className="h-7 w-7" />
          <span className="text-xl font-medium">Акции</span>
        </NavLink>

        <NavLink to="/calendar" className={({ isActive }) => `relative flex items-center gap-5 px-8 py-5 rounded-3xl transition-all duration-500 ${isActive ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl shadow-purple-600/60" : "text-purple-300 hover:bg-purple-600/40"}`}>
          <Calendar className="h-7 w-7" />
          <span className="text-xl font-medium">Календарь</span>
          {todayEventsCount > 0 && (
            <Badge className="absolute -top-3 -right-3 bg-red-600 text-white rounded-full w-10 h-10 flex items-center justify-center text-base font-bold shadow-2xl shadow-red-600/60 animate-pulse border-4 border-purple-900/80">
              {todayEventsCount > 9 ? "9+" : todayEventsCount}
            </Badge>
          )}
        </NavLink>

        {isPavlik && (
          <NavLink to="/disk" className={({ isActive }) => cn("flex items-center gap-5 px-8 py-5 rounded-3xl transition-all duration-500", isActive ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl shadow-purple-600/60" : "text-purple-300 hover:bg-purple-600/40")}>
            <HardDrive className="h-7 w-7" />
            <span className="text-xl font-medium">Диск</span>
          </NavLink>
        )}

        {isAdmin && (
          <NavLink 
            to="/admin/users" 
            className={({ isActive }) => `flex items-center gap-5 px-8 py-5 rounded-3xl transition-all duration-500 ${isActive ? "bg-gradient-to-r from-red-600 to-rose-600 text-white shadow-2xl shadow-red-600/60" : "text-purple-300 hover:bg-purple-600/40"}`}
          >
            <Shield className="h-7 w-7" />
            <span className="text-xl font-medium">Админка</span>
          </NavLink>
        )}

        {isPavlik && (
          <NavLink to="/settings" className={({ isActive }) => `flex items-center gap-5 px-8 py-5 rounded-3xl transition-all duration-500 ${isActive ? "bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl shadow-purple-600/60" : "text-purple-300 hover:bg-purple-600/40"}`}>
            <Settings className="h-7 w-7" />
            <span className="text-xl font-medium">Настройки</span>
          </NavLink>
        )}
      </nav>

      <div className="px-6 pb-10">
        <div className="bg-gradient-to-r from-purple-600/40 to-pink-600/40 backdrop-blur-xl rounded-3xl p-5 border border-purple-500/50 mb-6 shadow-2xl shadow-purple-600/30">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl font-bold text-white shadow-2xl">
              {user ? initials(user.name) : "???"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-lg truncate">{user?.name || "Пользователь"}</p>
              <p className="text-green-300 text-sm flex items-center gap-2">
                <Circle className="h-3 w-3 fill-current animate-pulse" />
                Онлайн • {organization?.shortName || "ОРВ ВСКС"}
              </p>
            </div>
          </div>
        </div>

        <button 
          onClick={handleLogout} 
          className="w-full flex items-center justify-center gap-4 py-5 bg-gradient-to-r from-red-600/50 to-pink-700/50 hover:from-red-600/70 hover:to-pink-700/70 backdrop-blur-xl text-red-300 rounded-3xl transition-all duration-500 font-medium text-lg border border-red-500/60 shadow-2xl shadow-red-600/40"
        >
          <LogOut className="h-7 w-7" />
          Выйти из системы
        </button>
      </div>
    </aside>
  );
}